import {z} from 'zod';
import {SchemaValidationErrorLabel} from '@netx/core-schema';
import {PermissionName} from '@netx/core-permissions-next/constants';
import {UserLevel, UserGroupItemType} from '../../constants';
import {UserMessage} from '../../locales';

export enum UserSource {
	INTERNAL = 0,
	LDAP = 1,
	SAML = 2,
	PING_ONE = 3,
	LIVE_CONNECT = 4,
	NETX = 5,
}

export const UserSchema = z.object({
	appPermissions: z.array(z.nativeEnum(PermissionName)),

	address1: z.string(),
	address2: z.string(),
	city: z.string(),
	country: z.string().nullable(),
	email: z
		.string()
		.email()
		.nonempty({message: SchemaValidationErrorLabel.REQUIRED}),
	/** eg: 2003-11-13 */
	expirationDate: z.string().nullable(),
	groupIds: z.array(z.number()),
	groupNames: z.array(z.string()),
	login: z.string(),
	/** first name */
	name1: z.string().nonempty({message: SchemaValidationErrorLabel.REQUIRED}),
	/** middle name */
	name2: z.string(),
	/** last name */
	name3: z.string().nonempty({message: SchemaValidationErrorLabel.REQUIRED}),
	/** this value is generally only used with Japanese names */
	name4: z.string(),
	/** whether this user should receive email from the application */
	notify: z.union([z.literal(1), z.literal(0)]),
	organization: z.string(),
	/** password */
	password1: z.string(),
	/** confirm password */
	password2: z.string(),
	/** home phone */
	phone1: z.string(),
	/** work phone */
	phone2: z.string(),
	/** cell phone */
	phone3: z.string(),
	/** fax? */
	phone4: z.string(),
	/** parallel arrays of arbitrary key/values */
	propertyNames: z.array(z.string()).nullable(),
	propertyValues: z.array(z.string()).nullable(),
	state: z.string().nullable(),
	type: z.nativeEnum(UserLevel),
	userId: z.number().int(),
	userSource: z.nativeEnum(UserSource),
	/** postal code */
	zip: z.string(),
	disabled: z.boolean(),
	// TODO: look into zod validator for date strings
	loginDate: z.string(),
	isLoggedIn: z.boolean(),
	canCreateAPITokens: z.boolean().optional(),
});

export default UserSchema;

export const UserField = UserSchema.keyof().enum;

export type IUser = z.infer<typeof UserSchema>;

// Schema for creating/editing users - only 4 fields required, rest optional
export const UserSchemaNew = UserSchema;

export type IUserNew = z.infer<typeof UserSchemaNew>;

export const UserFormSchema = UserSchemaNew.superRefine((data, ctx) => {
	if (data.userId === 0) {
		if (!data.login) {
			ctx.addIssue({
				code: z.ZodIssueCode.too_small,
				minimum: 1,
				inclusive: true,
				type: 'string',
				path: ['login'],
				message: SchemaValidationErrorLabel.REQUIRED,
			});
		} else if (/\s/.test(data.login)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: UserMessage.NO_SPACES,
				path: ['login'],
			});
		}
	}
});

export type IUserFormValues = z.infer<typeof UserFormSchema>;

export type UserGroupItem = {
	email: string;
	name: string;
	id: number;
	type: UserGroupItemType;
};
